import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  ArrowLeft, FileText, Bell, Trash2, Link2, ExternalLink
} from 'lucide-react';
import ReferralEditor from '../components/ReferralEditor';
import MeetingNoticeEditor from '../components/MeetingNoticeEditor';

import { API_URL } from '../utils/api';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  finalized: { label: 'Finalized', color: 'bg-green-50 text-green-700' },
  archived: { label: 'Archived', color: 'bg-gray-50 text-gray-500' },
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'finalized', label: 'Finalized' },
  { value: 'archived', label: 'Archived' },
];

const TYPE_CONFIG = {
  referral: { label: 'Referral', icon: FileText, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  meeting_notice: { label: 'Meeting Notice', icon: Bell, color: 'bg-blue-50 text-blue-700 border-blue-200' },
};

export default function LinkedDocumentDetail() {
  const { documentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchDocument = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/documents/${documentId}`, { withCredentials: true });
      setDoc(res.data);
    } catch (err) {
      console.error('Failed to fetch document', err);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => { fetchDocument(); }, [fetchDocument]);

  const handleSaveContent = async (content) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API_URL}/api/documents/${documentId}`, { content }, { withCredentials: true });
      setDoc(res.data);
    } catch (err) {
      console.error('Failed to save', err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await axios.patch(`${API_URL}/api/documents/${documentId}`, { status: newStatus }, { withCredentials: true });
      setDoc(res.data);
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this document? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/documents/${documentId}`, { withCredentials: true });
      navigate('/products/iep-met');
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="animate-pulse text-[#595959]">Loading document...</div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#595959] mb-4">Document not found.</p>
          <Link to="/products/iep-met" className="text-[#3B6A9B] hover:underline text-sm">Back to IEP/MET</Link>
        </div>
      </div>
    );
  }

  const typeConf = TYPE_CONFIG[doc.document_type] || TYPE_CONFIG.referral;
  const statusConf = STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft;
  const TypeIcon = typeConf.icon;

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="linked-document-detail">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#E5E5E5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <Link to="/products/iep-met" className="text-[#595959] hover:text-[#1C1C1C] transition-colors" data-testid="back-to-product">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${typeConf.color}`}>
              <TypeIcon className="h-3 w-3" />{typeConf.label}
            </div>
            <h1 className="font-outfit text-base sm:text-lg font-semibold text-[#1C1C1C] truncate" data-testid="document-title">{doc.title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Select value={doc.status} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-[120px] rounded-xl text-xs h-8" data-testid="document-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Badge className={`${statusConf.color} text-xs`} data-testid="document-status-badge">{statusConf.label}</Badge>
            <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-xl h-8 w-8 p-0" data-testid="delete-document-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Linked Cases */}
      {doc.linked_case_ids?.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-4">
          <div className="flex items-center gap-2 text-xs text-[#595959]">
            <Link2 className="h-3.5 w-3.5" />
            <span>Linked to:</span>
            {doc.linked_case_ids.map(cid => (
              <Link key={cid} to={`/products/iep-met/cases/${cid}`} className="text-[#3B6A9B] hover:underline flex items-center gap-1" data-testid={`linked-case-${cid}`}>
                {cid.slice(-8)}<ExternalLink className="h-3 w-3" />
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {doc.document_type === 'referral' && (
          <ReferralEditor document={doc} student={doc.student} onSave={handleSaveContent} saving={saving} />
        )}
        {doc.document_type === 'meeting_notice' && (
          <MeetingNoticeEditor document={doc} student={doc.student} onSave={handleSaveContent} saving={saving} />
        )}
      </div>
    </div>
  );
}
